(function (global) {
  'use strict';

  class MAPromotionCarousel {
    constructor(root, environment) {
      this.root = root;
      this.environment = environment || global;
      this.document = root.ownerDocument || global.document;
      this.slides = Array.from(root.querySelectorAll('[data-ma-slide]'));
      this.tabs = Array.from(root.querySelectorAll('[data-ma-slide-tab]'));
      this.index = -1;
      this.timer = null;
      this.transitionTimer = null;
      this.transitionDuration = 1000;
      this.hovered = false;
      this.focused = false;
      this.touchStartX = null;
      this.reducedMotion = Boolean(this.environment.matchMedia && this.environment.matchMedia('(prefers-reduced-motion: reduce)').matches);
      this.canHover = Boolean(this.environment.matchMedia && this.environment.matchMedia('(hover: hover) and (pointer: fine)').matches);
      this.autoplay = root.dataset.autoplay === 'true' && !this.reducedMotion && this.slides.length > 1;
      this.interval = Number(root.dataset.interval) || 5000;
      this.bind();
      this.goTo(0, false);
      this.start();
    }

    bind() {
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => {
          const direction = this.index < 0 || index >= this.index ? 1 : -1;
          this.goTo(index, false, direction);
          this.restart();
        });
      });
      this.root.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        event.key === 'ArrowRight' ? this.next() : this.previous();
        this.restart();
      });
      this.root.addEventListener('mouseenter', () => {
        if (!this.canHover) return;
        this.hovered = true;
        this.stop();
      });
      this.root.addEventListener('mouseleave', () => {
        if (!this.canHover) return;
        this.hovered = false;
        this.start();
      });
      this.root.addEventListener('focusin', (event) => {
        if (event.target && event.target.matches && !event.target.matches(':focus-visible')) return;
        this.focused = true;
        this.stop();
      });
      this.root.addEventListener('focusout', (event) => {
        if (event.relatedTarget && this.root.contains && this.root.contains(event.relatedTarget)) return;
        this.focused = false;
        this.start();
      });
      this.root.addEventListener('touchstart', (event) => {
        this.hovered = false;
        this.focused = false;
        this.touchStartX = event.changedTouches && event.changedTouches[0] ? event.changedTouches[0].clientX : null;
      }, { passive: true });
      this.root.addEventListener('touchend', (event) => {
        if (this.touchStartX === null || !event.changedTouches || !event.changedTouches[0]) return;
        const distance = event.changedTouches[0].clientX - this.touchStartX;
        this.touchStartX = null;
        if (Math.abs(distance) < 40) return;
        distance < 0 ? this.next() : this.previous();
        this.restart();
      }, { passive: true });
      if (this.document && this.document.addEventListener) {
        this.document.addEventListener('visibilitychange', () => {
          this.document.hidden ? this.stop() : this.start();
        });
      }
    }

    finishTransition() {
      if (this.transitionTimer) {
        this.environment.clearTimeout(this.transitionTimer);
        this.transitionTimer = null;
      }
      this.slides.forEach((slide, slideIndex) => {
        slide.classList.remove('ma-slide-leaving-left', 'ma-slide-entering-right', 'ma-slide-leaving-right', 'ma-slide-entering-left');
        const selected = slideIndex === this.index;
        slide.hidden = !selected;
        slide.setAttribute('aria-hidden', String(!selected));
        if (selected) slide.removeAttribute('inert');
        else slide.setAttribute('inert', '');
      });
    }

    goTo(index, focusTab, direction) {
      if (!this.slides.length) return;
      const targetIndex = (index + this.slides.length) % this.slides.length;
      const previousIndex = this.index;
      if (targetIndex === previousIndex) return;
      if (this.transitionTimer) this.finishTransition();
      this.index = targetIndex;

      const animate = previousIndex >= 0 && !this.reducedMotion && this.environment.setTimeout;
      if (animate) {
        const movingForward = direction !== -1;
        const outgoing = this.slides[previousIndex];
        const incoming = this.slides[this.index];
        this.slides.forEach((slide, slideIndex) => {
          const inTransition = slideIndex === previousIndex || slideIndex === this.index;
          slide.hidden = !inTransition;
          slide.setAttribute('aria-hidden', String(slideIndex !== this.index));
          if (slideIndex === this.index) slide.removeAttribute('inert');
          else slide.setAttribute('inert', '');
        });
        outgoing.classList.add(movingForward ? 'ma-slide-leaving-left' : 'ma-slide-leaving-right');
        incoming.classList.add(movingForward ? 'ma-slide-entering-right' : 'ma-slide-entering-left');
        this.transitionTimer = this.environment.setTimeout(() => this.finishTransition(), this.transitionDuration);
      } else {
        this.finishTransition();
      }
      this.tabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === this.index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      if (focusTab && this.tabs[this.index] && this.tabs[this.index].focus) this.tabs[this.index].focus();
    }

    next() {
      this.goTo(this.index + 1, false, 1);
    }

    previous() {
      this.goTo(this.index - 1, false, -1);
    }

    start() {
      if (!this.autoplay || this.timer || this.hovered || this.focused || (this.document && this.document.hidden)) return;
      this.timer = this.environment.setInterval(() => this.next(), this.interval);
    }

    stop() {
      if (!this.timer) return;
      this.environment.clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }
  }

  function initialise() {
    if (!global.document) return;
    global.document.querySelectorAll('[data-ma-promotion-carousel]').forEach((root) => {
      if (root.dataset.carouselReady === 'true') return;
      root.dataset.carouselReady = 'true';
      new MAPromotionCarousel(root);
    });
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = MAPromotionCarousel;
  if (global.document) {
    if (global.document.readyState === 'loading') global.document.addEventListener('DOMContentLoaded', initialise);
    else initialise();
    global.document.addEventListener('shopify:section:load', initialise);
  }
})(typeof window !== 'undefined' ? window : globalThis);
